/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import {
  buildImageFormData,
  normalizeSkuCreationFormData,
} from "@/components/templates/ProductForm/form-data";
import { normalizeStockMovementData } from "@/components/templates/StockMovementModal/form-data";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import { normalizeVariationsToBackend } from "@/helpers/variation-helpers";
import { useProductFormStateContext } from "@/contexts/product-form-state-context";
import useRemoveKit from "@/hooks/requests/kit/use-delete-kit";
import useUpdateProduct from "@/hooks/requests/product/use-update-product";
import useCreateProductImage from "@/hooks/requests/product-images/use-create-product-image";
import useRemoveProductImage from "@/hooks/requests/product-images/use-remove-product-image";
import useCreateSKU from "@/hooks/requests/sku/use-create-sku";
import useRemoveSKU from "@/hooks/requests/sku/use-remove-sku";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import IProduct, { IProductInput } from "@/interfaces/product.interface";
import { ISku, ISkuInput } from "@/interfaces/sku.interface";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import productImageService from "@/services/product-images.service";

import useCreateKit from "../kit/use-create-kit";
import useCreateStockMovement from "../stock/use-create-stock";
import { useSaveLinkedCategories, useGetLinkedCategories } from "../linked-product-category";
import linkedProductCategoryService from "@/services/linked-product-category.service";

type Props = {
  messages: ProductFormPageMessages;
  product: IProduct | null;
  customerId: string | null;
};

const useUpdateProductWithSkus = ({ product, messages, customerId }: Props) => {
  const { startLoading, endLoading, isLoading, loadingIcon } = useLoading();
  const { remove: removeProductImage } = useRemoveProductImage();
  const { remove: removeSku } = useRemoveSKU();
  const { create: createSku } = useCreateSKU();
  const { create: createStockMovement } = useCreateStockMovement();
  const { create: createKit } = useCreateKit();
  const { removeKit } = useRemoveKit();
  const { update } = useUpdateProduct();
  const { saveCategories } = useSaveLinkedCategories();
  const { data: linkedCategories } = useGetLinkedCategories({ productId: product?.id || null });
  const router = useNavigate();
  const { lang } = useLanguage();
  const { imagesToDelete, setImagesToDelete } = useProductFormStateContext();

  const {
    create: createImg,
    isLoading: isLoadingUpdateImg,
    loadingIcon: loadingUpdateImgIcon,
  } = useCreateProductImage();

  const { mutate } = useMutate();

  const handleUpdate = async (_formData: ProductFormData, showSuccessMessage = true) => {
    // Se não há produto mas há productId no formData, construir produto temporário
    const productId = product?.id || _formData.id;
    if (!productId) return;
    
    // Se não há produto completo, construir um temporário a partir do formData
    const productToUse = product || ({
      id: productId,
      ..._formData,
    } as unknown as IProduct);

    startLoading();

    try {
      const onError = async () => {
        // onError não deve chamar endLoading aqui pois será chamado no finally
      };

      // Verifique se `productToUse.productImages` existe antes de usar `find`
      const previousMainImage = productToUse.productImages?.find((img) => img.main) || null;

      const updatedSkus = await Promise.all(
        _formData.skus.map(async (rawSku) => {
          const skuData = { ...rawSku, videoUrl: (_formData.videoUrl || "").slice(0, 1000) };
          let createSkuResponse: ISku | undefined;
          if (!skuData.id) {
            // SKU não tem ID, então deve ser criado
            const normalizedSkuData = normalizeSkuCreationFormData(
              skuData,
              productToUse,
              Number(customerId),
              true
            );

            createSkuResponse = await createSku({
              formData: normalizedSkuData,
              errorMessage: messages.skuCreateError,
              onError,
            });

            if (createSkuResponse && createSkuResponse.kit) {
              await Promise.all(
                skuData.kitComponents.map(async (kit) => {
                  await createKit({
                    formData: { ...kit, skuId: createSkuResponse!.id },
                    onSuccess: () => {},
                    onError,
                  });
                })
              );
            }

            return {
              ...normalizedSkuData,
              id: createSkuResponse.id,
              productId: productToUse.id,
            };
          } else {
            // SKU tem ID, então deve ser atualizado
            const normalizedSkuData = normalizeSkuCreationFormData(
              skuData,
              productToUse,
              Number(customerId),
              false
            );

            return normalizedSkuData;
          }
        })
      );

      // Atualizar o produto
      const onSuccess = async () => {
        // Criar ou atualizar movimentações de estoque após a atualização do produto
        await Promise.all(
          _formData.skus.map(async (skuData, index) => {
            const skuId = updatedSkus[index].id;
            await handleStockMovementCreation(skuData, skuId);
          })
        );

        endLoading();
        mutate(SWR_KEYS.getProductById);
        router(ROUTE_URLS.products(lang));
      };

      // Remover campos que não fazem parte de IProductInput
      const { 
        productImages, 
        customerId: _, 
        selectedCategories,
        isEditing,
        isEditingSku,
        sku,
        ...rest 
      } = _formData;
      const normalizedProductVariations = rest.variations
        ? normalizeVariationsToBackend(rest.variations)
        : [];

      // Incluir código, brand e ncm (obrigatórios no PUT)
      // Usar o código do produto original (productToUse.code) ou do formData
      const productCode = productToUse.code || _formData.code || "";
      
      // Garantir que todos os campos obrigatórios estejam presentes
      const normalizeUpdateData: IProductInput = { 
        id: productToUse.id, // ID do produto
        customerId: Number(customerId), // Garantir customerId correto do hook
        active: _formData.active ?? true,
        code: productCode, // Obrigatório no PUT (deve ser o código original do produto)
        externalReference: _formData.externalReference || "",
        title: _formData.title || "",
        shortDescription: (_formData.shortDescription || "").slice(0, 500),
        description: (_formData.description || "").slice(0, 4096),
        brand: _formData.brand || "", // Obrigatório (não nullable no modelo)
        ncm: _formData.ncm || "", // Obrigatório (não nullable no modelo)
        variationCode: _formData.variationCode || "",
        variations: normalizedProductVariations,
        warrantyTime: _formData.warrantyTime ?? null, // Preservar warrantyTime (pode ser null ou string)
        pickUpInStore: _formData.pickUpInStore ?? false,
        needComment: _formData.needComment ?? false,
        groupVariations: _formData.groupVariations ?? false,
        useShortDescriptionInSales: _formData.useShortDescriptionInSales ?? false,
        videoUrl: (_formData.videoUrl || "").slice(0, 1000),
        skus: updatedSkus, // SKUs atualizados
        productImages: null, // Imagens são gerenciadas separadamente
      };

      await update({
        _id: Number(productToUse.id) || 0,
        formData: normalizeUpdateData,
        successMessage: showSuccessMessage ? messages.productUpdateSuccess : "",
        errorMessage: messages.productUpdateError,
        onSuccess,
        onError,
      });

      // Remover SKUs marcados para deletar
      await Promise.all(
        _formData.skusToDelete.map(async (skuId) => {
          await removeSku({
            _id: skuId,
            onSuccess: () => {},
            errorMessage: messages.skuCreateError,
            onError,
          });
        })
      );

      // Remover kits marcados para deletar
      await Promise.all(
        _formData.skus
          .flatMap((sku) => sku.kitsToDelete)
          .filter((kitId) => kitId !== undefined && kitId !== null)
          .map(async (kitId) => {
            if (kitId) {
              await removeKit({
                _id: kitId,
                onSuccess: () => {},
                errorMessage: messages.deleteKitError,
                onError,
              });
            }
          })
      );

      // Remover imagens marcadas para deletar
      await Promise.all(
        imagesToDelete.map(async (imageId) => {
          await removeProductImage({
            _id: Number(imageId),
            errorMessage: messages.errorRemovePhoto,
            onError,
          });
        })
      );

      // Criar novas imagens e verificar a nova imagem principal
      let newMainImageId: number | undefined;
      if (_formData.productImages) {
        for (const image of _formData.productImages) {
          if ("file" in image) {
            const imageFormData = buildImageFormData(
              image as import("@/interfaces/product.interface").ImageData,
              productToUse.id.toString()
            );
            const createdImage = await createImg({
              formData: imageFormData,
              errorMessage: messages.imageUpdateError,
              onError,
            });

            if (image.main) {
              newMainImageId = createdImage.id;
            }
          } else if (image.main) {
            newMainImageId = image.id;
          }
        }
      }
      // Verifique se a imagem principal foi alterada
      if (newMainImageId && previousMainImage?.id !== newMainImageId) {
        // Chama o serviço para atualizar a imagem principal
        await productImageService.updateMainImage(productToUse.id, newMainImageId);
      }

      // Gerenciar categorias: REPLACE ALL (limpeza total + inserção)
      // 1) Limpar todos os vínculos existentes
      if (linkedCategories && linkedCategories.length > 0) {
        await Promise.all(
          linkedCategories.map(lc => 
            linkedProductCategoryService.deleteByCategory(productToUse.id, lc.categoryId)
          )
        );
      }

      // 2) Inserir apenas as categorias selecionadas
      if (_formData.selectedCategories.length > 0) {
        await saveCategories({
          data: {
            productId: productToUse.id,
            categoryIds: _formData.selectedCategories,
          },
        });
      }
    } finally {
      endLoading();
    }
  };

  const handleStockMovementCreation = async (_formData: ISkuInput, skuId: number) => {
    const stockMovementData = _formData.stockMovement;

    if (stockMovementData) {
      const normalizedStockMovementData = normalizeStockMovementData(stockMovementData, skuId);
      await createStockMovement({
        formData: normalizedStockMovementData,
        errorMessage: messages.errorCreateStockMessage,
      });
    }
  };

  return {
    handleUpdate,
    isLoading,
    loadingIcon,
    isLoadingUpdateImg,
    loadingUpdateImgIcon,
  };
};

export default useUpdateProductWithSkus;
